import React from 'react';
import PropTypes from 'prop-types';
import './index.scss';

/* Tab renders a single tab with a click event */
export default class Tab extends React.Component {
  static propTypes = {
    dropdownMenu: PropTypes.any,
    icon: PropTypes.any,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func,
    style: PropTypes.object,
    title: PropTypes.any.isRequired,
    type: PropTypes.oneOf(['plain', 'underline', 'month', 'sheet']),
  };

  static defaultProps = {
    dropdownMenu: null,
    icon: null,
    isSelected: false,
    onSelect: () => {},
    style: {},
    title: '',
    type: 'plain',
  };

  componentDidMount() {
    /**
     * If a dropdown menu is passed, calculate the top-left of
     * the bounding sheet tab and position the dropdown menu above it
     *
     * Note that if an offset (top, left, right, bottom)
     * is passed to the dropdownMenu (prop=menuStyle),
     * it must be in px to work properly here (see componentDidMount)
     */
    if (this.props.dropdownMenu) {
      // SetTimeout allows the css to take effect before calculating positions
      setTimeout(() => {
        if (!this._tab) {
          return; // _tab is null if unmounted already
        }
        const menu = this._tab.querySelector('.DropdownMenu-menu');

        // Get offset that was manually passed to dropdown
        // Slice off "px" and convert to number
        const passedTopOffsetToDropdown = Number(menu.style.top.slice(0, -2));
        const passedLeftOffsetToDropdown = Number(menu.style.left.slice(0, -2));

        // Get position of tab and menu, relative to viewport
        const tabViewportOffset = this._tab.getBoundingClientRect();
        const menuViewportOffset = menu.getBoundingClientRect();

        // Determine the difference in position between tab top-left and menu bottom-left
        const relativeDifferenceLeft =
          tabViewportOffset.left - menuViewportOffset.left;
        const relativeDifferenceTop =
          tabViewportOffset.top - menuViewportOffset.bottom;

        // Assign the new position to the dropdown menu (Why 2 * passed...OffsetToDropdown?
        // Because the relativeDifferenceLeft/Top is accounting for the passedTopOffsetToDropdown,
        // So we have to double it in order for the intended offset to work)
        menu.style.left = `${
          2 * passedLeftOffsetToDropdown + relativeDifferenceLeft
        }px`;
        menu.style.top = `${
          2 * passedTopOffsetToDropdown + relativeDifferenceTop
        }px`;
      }, 0);
    }
  }

  render() {
    // Build up a className from a list of classes
    const classList = [];
    const tabBaseClass = 'Tab';

    const typeClasses = {
      plain: '',
      underline: 'Tab--underline',
      month: 'Tab--month',
      sheet: 'Tab--sheet',
    };
    const typeClass = typeClasses[this.props.type];
    const isSelectedClass = this.props.isSelected ? 'Tab--selected' : '';

    classList.push(tabBaseClass, typeClass, isSelectedClass);

    // Strip out empty strings from class list before joining
    const filteredClassList = classList.filter((str) => str);
    const className = filteredClassList.join(' ');

    // Disable onClick functionality if selected
    const onClick = this.props.isSelected ? () => {} : this.props.onSelect;

    const icon = !this.props.icon ? null : (
      <span className={'Tab-icon'}>{this.props.icon}</span>
    );

    const dropdownMenu = !this.props.dropdownMenu ? null : (
      <span className={'Tab-dropdownMenu'}>{this.props.dropdownMenu}</span>
    );

    const hasAside = Boolean(icon || dropdownMenu);

    const aside = !hasAside ? null : (
      <span className={'Tab-aside'}>
        {icon}
        {dropdownMenu}
      </span>
    );

    const titleClasses = ['Tab-title'];

    if (hasAside) {
      titleClasses.push('Tab-title--withAside');
    }

    const title = (
      <span className={titleClasses.join(' ')}>{this.props.title}</span>
    );

    return (
      <span
        className={className}
        onClick={onClick}
        ref={(tab) => (this._tab = tab)}
        style={this.props.style}>
        <div className={'Tab-contents'}>
          {title}
          {aside}
        </div>
      </span>
    );
  }
}
